package com.castly.admin.ui.preview

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.dto.EpisodeResponse
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.shared.dto.CarouselItemDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Continue Watching hero card — renders a single "continue_watching" section.
 * Shows the most recent unfinished episode as a large hero card with progress bar.
 */
@Composable
fun PreviewContinueWatchingHero(
    title: String?,
    item: CarouselItemDto,
    onResume: (CarouselItemDto) -> Unit
) {
    val colors = AppTheme.colors
    // Parse progress from description ("Episode Title • 45%")
    val progressPercent = item.description?.substringAfterLast("• ")?.removeSuffix("%")?.toIntOrNull() ?: 0

    Div({
        style {
            marginBottom(Spacing.xxl)
        }
    }) {
        // Section title
        title?.let {
            Div({
                style {
                    fontSize(Typography.xl)
                    fontWeight("bold")
                    color(Color(colors.text))
                    marginBottom(Spacing.sm)
                }
            }) { Text(it) }
        }

        // Hero card
        Div({
            style {
                width(100.percent)
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                overflow("hidden")
                cursor("pointer")
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.1)")
            }
            onClick { onResume(item) }
        }) {
            // Cover image
            Div({
                style {
                    width(100.percent)
                    height(160.px)
                    backgroundColor(Color(colors.border))
                    position(Position.Relative)
                    overflow("hidden")
                }
            }) {
                if (item.coverArt != null) {
                    Img(item.coverArt!!) {
                        attr("loading", "lazy")
                        attr("decoding", "async")
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("object-fit", "cover")
                        }
                    }
                } else {
                    Div({
                        style {
                            width(100.percent)
                            height(100.percent)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            fontSize(48.px)
                            color(Color(colors.textMuted))
                        }
                    }) { Text("\u266A") }
                }
                // Play overlay
                Div({
                    style {
                        position(Position.Absolute)
                        right(12.px)
                        bottom(12.px)
                        width(40.px)
                        height(40.px)
                        backgroundColor(Color("#7c3aed"))
                        borderRadius(50.percent)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        color(Color("#ffffff"))
                        fontSize(Typography.lg)
                    }
                }) { Text("\u25B6") }
            }

            // Info + progress
            Div({
                style {
                    padding(Spacing.md)
                }
            }) {
                // Title
                Div({
                    style {
                        fontSize(Typography.lg)
                        fontWeight("600")
                        color(Color(colors.text))
                        marginBottom(Spacing.xs)
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
                }) { Text(item.title) }

                // Episode subtitle
                item.description?.substringBeforeLast(" \u2022 ")?.let { episodeTitle ->
                    Div({
                        style {
                            fontSize(Typography.sm)
                            color(Color(colors.textMuted))
                            marginBottom(Spacing.md)
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                    }) { Text(episodeTitle) }
                }

                // Progress bar
                Div({
                    style {
                        width(100.percent)
                        height(4.px)
                        backgroundColor(Color(colors.border))
                        borderRadius(2.px)
                        overflow("hidden")
                    }
                }) {
                    Div({
                        style {
                            width(progressPercent.percent)
                            height(100.percent)
                            backgroundColor(Color("#7c3aed"))
                            borderRadius(2.px)
                        }
                    })
                }

                // Progress text
                Div({
                    style {
                        fontSize(Typography.xs)
                        color(Color(colors.textMuted))
                        marginTop(Spacing.xs)
                        textAlign("right")
                    }
                }) { Text("$progressPercent% complete") }
            }
        }
    }
}

/**
 * Continue Watching carousel — renders a "continue_watching_carousel" section.
 * Shows up to 5 unfinished episodes as horizontal scrollable cards with progress bars.
 */
@Composable
fun PreviewContinueWatchingCarousel(
    title: String?,
    description: String?,
    items: List<CarouselItemDto>,
    onResume: (CarouselItemDto) -> Unit,
    onSeeAll: () -> Unit
) {
    val colors = AppTheme.colors

    Div({
        style {
            marginBottom(Spacing.xxl)
        }
    }) {
        // Section header with See All
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(Spacing.sm)
            }
        }) {
            title?.let {
                Div({
                    style {
                        fontSize(Typography.xl)
                        fontWeight("bold")
                        color(Color(colors.text))
                    }
                }) { Text(it) }
            }
            Button({
                onClick { onSeeAll() }
                style {
                    backgroundColor(Color.transparent)
                    border(0.px)
                    color(Color("#7c3aed"))
                    cursor("pointer")
                    fontSize(Typography.sm)
                    fontWeight("600")
                    padding(0.px)
                }
            }) { Text("See All >") }
        }

        description?.let { desc ->
            Div({
                style {
                    fontSize(Typography.base)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.md)
                }
            }) { Text(desc) }
        }

        // Horizontal scrolling carousel
        Div({
            style {
                display(DisplayStyle.Flex)
                property("gap", "12px")
                overflowX("auto")
                paddingBottom(Spacing.sm)
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            items.forEach { item ->
                val progressPercent = item.description?.substringAfterLast("\u2022 ")?.removeSuffix("%")?.toIntOrNull() ?: 0

                Div({
                    style {
                        minWidth(140.px)
                        width(140.px)
                        flexShrink(0)
                        cursor("pointer")
                    }
                    onClick { onResume(item) }
                }) {
                    // Cover art with play button
                    Div({
                        style {
                            width(140.px)
                            height(140.px)
                            backgroundColor(Color(colors.border))
                            borderRadius(BorderRadius.lg)
                            marginBottom(Spacing.xs)
                            overflow("hidden")
                            position(Position.Relative)
                        }
                    }) {
                        if (item.coverArt != null) {
                            Img(item.coverArt!!) {
                                attr("loading", "lazy")
                                attr("decoding", "async")
                                style {
                                    width(100.percent)
                                    height(100.percent)
                                    property("object-fit", "cover")
                                }
                            }
                        } else {
                            Div({
                                style {
                                    width(100.percent)
                                    height(100.percent)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    fontSize(40.px)
                                    color(Color(colors.textMuted))
                                }
                            }) { Text("\u266A") }
                        }
                        // Small play overlay
                        Div({
                            style {
                                position(Position.Absolute)
                                right(6.px)
                                bottom(6.px)
                                width(28.px)
                                height(28.px)
                                backgroundColor(Color("#7c3aed"))
                                borderRadius(50.percent)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                color(Color("#ffffff"))
                                fontSize(Typography.sm)
                            }
                        }) { Text("\u25B6") }
                    }

                    // Progress bar
                    Div({
                        style {
                            width(100.percent)
                            height(3.px)
                            backgroundColor(Color(colors.border))
                            borderRadius(2.px)
                            overflow("hidden")
                            marginBottom(Spacing.xs)
                        }
                    }) {
                        Div({
                            style {
                                width(progressPercent.percent)
                                height(100.percent)
                                backgroundColor(Color("#7c3aed"))
                                borderRadius(2.px)
                            }
                        })
                    }

                    // Title
                    Div({
                        style {
                            fontSize(Typography.base)
                            fontWeight("600")
                            color(Color(colors.text))
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                            marginBottom(2.px)
                        }
                    }) { Text(item.title) }

                    // Episode subtitle
                    item.description?.substringBeforeLast(" \u2022 ")?.let { episodeTitle ->
                        Div({
                            style {
                                fontSize(Typography.xs)
                                color(Color(colors.textMuted))
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                        }) { Text(episodeTitle) }
                    }
                }
            }

            if (items.isEmpty()) {
                Div({
                    style {
                        minWidth(150.px)
                        height(140.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        backgroundColor(Color(colors.surfaceHover))
                        borderRadius(BorderRadius.lg)
                        color(Color(colors.textMuted))
                        fontSize(Typography.sm)
                    }
                }) { Text("No items") }
            }
        }
    }
}

/**
 * Continue Watching full list view (T047)
 * Shows all unfinished streams via GET /api/v1/stream-progress with pagination.
 */
@Composable
fun PreviewContinueWatchingListView(
    apiClient: ApiClient,
    selectedLanguage: String,
    onResumeEpisode: (EpisodeResponse) -> Unit
) {
    val colors = AppTheme.colors
    var items by remember { mutableStateOf<List<EpisodeResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentPage by remember { mutableStateOf(1) }
    var totalCount by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    val hasMore = items.size < totalCount

    fun loadData(page: Int = 1, append: Boolean = false) {
        scope.launch {
            if (append) loadingMore = true else loading = true
            error = null

            when (val result = apiClient.getContinueWatching(page = page, pageSize = 20)) {
                is ApiClient.ApiResult.Success -> {
                    if (append) {
                        items = items + result.data.items
                    } else {
                        items = result.data.items
                    }
                    totalCount = result.data.total
                    currentPage = page
                }
                is ApiClient.ApiResult.Error -> {
                    error = result.message
                }
            }
            loading = false
            loadingMore = false
        }
    }

    LaunchedEffect(Unit) {
        loadData()
    }

    Div({
        style {
            height(100.percent)
            overflow("auto")
            backgroundColor(Color(colors.surfaceHover))
        }
    }) {
        when {
            loading -> LoadingSpinner()
            error != null -> ErrorDisplay(
                code = "ERROR",
                message = error!!,
                onRetry = { loadData() }
            )
            items.isEmpty() -> {
                // Empty state
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        height(100.percent)
                        padding(Spacing.xxl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) {
                    Div({ style { fontSize(48.px); marginBottom(Spacing.lg) } }) { Text("\u25B6") }
                    Div({ style { fontSize(Typography.lg); fontWeight("600"); marginBottom(Spacing.sm) } }) {
                        Text("No episodes in progress")
                    }
                    Div({ style { fontSize(Typography.base) } }) {
                        Text("Start listening to see your progress here")
                    }
                }
            }
            else -> {
                Div({ style { padding(Spacing.md) } }) {
                    items.forEach { item ->
                        // List item row
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(Spacing.md)
                                padding(Spacing.md)
                                backgroundColor(Color(colors.surface))
                                borderRadius(BorderRadius.md)
                                marginBottom(Spacing.sm)
                                cursor("pointer")
                            }
                            onClick { onResumeEpisode(item) }
                        }) {
                            // Cover art
                            Div({
                                style {
                                    width(56.px)
                                    height(56.px)
                                    minWidth(56.px)
                                    backgroundColor(Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    overflow("hidden")
                                    position(Position.Relative)
                                }
                            }) {
                                if (item.imageUrl != null) {
                                    Img(item.imageUrl!!) {
                                        attr("loading", "lazy")
                                        attr("decoding", "async")
                                        style {
                                            width(100.percent)
                                            height(100.percent)
                                            property("object-fit", "cover")
                                        }
                                    }
                                } else {
                                    Div({
                                        style {
                                            width(100.percent)
                                            height(100.percent)
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.Center)
                                            justifyContent(JustifyContent.Center)
                                            fontSize(24.px)
                                            color(Color(colors.textMuted))
                                        }
                                    }) { Text("\u266A") }
                                }
                            }

                            // Text info
                            Div({
                                style {
                                    flex(1)
                                    overflow("hidden")
                                }
                            }) {
                                // Content title
                                Div({
                                    style {
                                        fontSize(Typography.base)
                                        fontWeight("600")
                                        color(Color(colors.text))
                                        property("overflow", "hidden")
                                        property("text-overflow", "ellipsis")
                                        property("white-space", "nowrap")
                                        marginBottom(2.px)
                                    }
                                }) { Text(item.title) }

                                // Episode description
                                item.description?.let { desc ->
                                    Div({
                                        style {
                                            fontSize(Typography.sm)
                                            color(Color(colors.textMuted))
                                            property("overflow", "hidden")
                                            property("text-overflow", "ellipsis")
                                            property("white-space", "nowrap")
                                            marginBottom(Spacing.xs)
                                        }
                                    }) { Text(desc) }
                                }

                                // Progress bar
                                val progressPercent = item.engagement?.progressPercent ?: 0
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        gap(Spacing.sm)
                                    }
                                }) {
                                    Div({
                                        style {
                                            flex(1)
                                            height(3.px)
                                            backgroundColor(Color(colors.border))
                                            borderRadius(2.px)
                                            overflow("hidden")
                                        }
                                    }) {
                                        Div({
                                            style {
                                                width(progressPercent.percent)
                                                height(100.percent)
                                                backgroundColor(Color("#7c3aed"))
                                                borderRadius(2.px)
                                            }
                                        })
                                    }
                                    Div({
                                        style {
                                            fontSize(Typography.xs)
                                            color(Color(colors.textMuted))
                                            minWidth(35.px)
                                            textAlign("right")
                                        }
                                    }) { Text("$progressPercent%") }
                                }
                            }

                            // Play button
                            Div({
                                style {
                                    width(32.px)
                                    height(32.px)
                                    backgroundColor(Color("#7c3aed"))
                                    borderRadius(50.percent)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.Center)
                                    color(Color("#ffffff"))
                                    fontSize(Typography.sm)
                                    flexShrink(0)
                                }
                            }) { Text("\u25B6") }
                        }
                    }

                    // Load more button
                    if (hasMore) {
                        Button({
                            onClick {
                                if (!loadingMore) loadData(currentPage + 1, append = true)
                            }
                            style {
                                width(100.percent)
                                padding(Spacing.md)
                                backgroundColor(Color(colors.surface))
                                color(Color("#7c3aed"))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                cursor("pointer")
                                fontSize(Typography.base)
                                fontWeight("600")
                                marginTop(Spacing.sm)
                            }
                        }) {
                            Text(if (loadingMore) "Loading..." else "Load More")
                        }
                    }
                }
            }
        }
    }
}
